// push-subscription-store.ts — persist THIS device's Web Push subscription to
// public.push_subscriptions (owner RLS). Device-scoped infra, intentionally OUTSIDE
// the synced life_os_items blob. Reuses a persisted-session Supabase client
// (same pattern as ai-runtime / attachment-storage-io). Fails soft, never throws.

/** Minimal shape of PushSubscription.toJSON(). */
export type PushSubscriptionJsonLike = {
  endpoint?: string;
  keys?: { p256dh?: string; auth?: string };
} | null;

export type PushSubscriptionRow = { endpoint: string; p256dh: string; auth: string };

/** Pure: pull the row fields out of a subscription JSON; null when incomplete. */
export function extractSubscriptionRow(json: PushSubscriptionJsonLike): PushSubscriptionRow | null {
  if (!json?.endpoint || !json.keys?.p256dh || !json.keys?.auth) return null;
  return { endpoint: json.endpoint, p256dh: json.keys.p256dh, auth: json.keys.auth };
}

type DbClient = {
  from: (table: string) => {
    upsert: (row: Record<string, unknown>, opts?: Record<string, unknown>) => Promise<{ error: unknown }>;
    delete: () => { eq: (col: string, val: string) => Promise<{ error: unknown }> };
  };
};

let cached: DbClient | null = null;

async function getClient(): Promise<DbClient | null> {
  if (cached) return cached;
  try {
    const { createClient } = await import("@supabase/supabase-js");
    const url = import.meta.env.VITE_SUPABASE_URL as string | undefined;
    const anon = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;
    if (!url || !anon) return null;
    cached = createClient(url, anon, {
      auth: { persistSession: true, autoRefreshToken: false },
    }) as unknown as DbClient;
    return cached;
  } catch {
    return null;
  }
}

/** Upsert the device subscription for the signed-in user. Returns true on success. */
export async function savePushSubscription(
  json: PushSubscriptionJsonLike,
  userId: string,
): Promise<boolean> {
  const row = extractSubscriptionRow(json);
  if (!row || !userId) return false;
  const client = await getClient();
  if (!client) return false;
  const userAgent = typeof navigator !== "undefined" ? navigator.userAgent : null;
  const { error } = await client
    .from("push_subscriptions")
    .upsert({ user_id: userId, ...row, user_agent: userAgent }, { onConflict: "endpoint" });
  return !error;
}

/** Best-effort delete of a device subscription row by endpoint. */
export async function deletePushSubscription(endpoint: string): Promise<void> {
  if (!endpoint) return;
  const client = await getClient();
  if (!client) return;
  try {
    await client.from("push_subscriptions").delete().eq("endpoint", endpoint);
  } catch {
    // best-effort
  }
}
